import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * Reusable Pagination component for client-side pagination.
 *
 * Props:
 *  - page: current page (1-based)
 *  - pageSize: items per page
 *  - totalItems: total items across all pages
 *  - onPageChange(nextPage): callback
 *  - onPageSizeChange(nextSize): callback (optional)
 *  - pageSizeOptions: array of page-size choices (default [10, 25, 50, 100])
 */
const Pagination = ({
  page = 1,
  pageSize = 10,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, totalItems);

  const goto = (p) => {
    const target = Math.min(Math.max(1, p), totalPages);
    if (target !== safePage) onPageChange?.(target);
  };

  // Generate compact page list with ellipsis for large totals
  const pageButtons = (() => {
    const out = [];
    const window = 1; // pages either side of current
    const add = (v) => out.push(v);
    add(1);
    if (safePage - window > 2) add('…');
    for (let p = Math.max(2, safePage - window); p <= Math.min(totalPages - 1, safePage + window); p++) {
      add(p);
    }
    if (safePage + window < totalPages - 1) add('…');
    if (totalPages > 1) add(totalPages);
    return out;
  })();

  return (
    <div
      className="flex flex-col sm:flex-row items-center justify-between gap-3 px-3 py-2 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700"
      data-testid="pagination"
    >
      <div className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-2">
        <span>
          Menampilkan <strong className="text-slate-800 dark:text-slate-200">{start}</strong>–
          <strong className="text-slate-800 dark:text-slate-200">{end}</strong> dari{' '}
          <strong className="text-slate-800 dark:text-slate-200">{totalItems}</strong>
        </span>
        {onPageSizeChange && (
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="text-xs border border-slate-300 dark:border-slate-600 rounded px-2 py-0.5 bg-white dark:bg-slate-800"
            data-testid="pagination-page-size"
          >
            {pageSizeOptions.map((s) => (
              <option key={s} value={s}>
                {s} / hal
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="flex items-center gap-1" data-testid="pagination-controls">
        <button
          onClick={() => goto(1)}
          disabled={safePage === 1}
          className="h-7 w-7 flex items-center justify-center rounded border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pagination-first"
          title="Halaman pertama"
        >
          <ChevronsLeft className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => goto(safePage - 1)}
          disabled={safePage === 1}
          className="h-7 w-7 flex items-center justify-center rounded border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pagination-prev"
          title="Sebelumnya"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>

        {pageButtons.map((p, idx) =>
          p === '…' ? (
            <span key={`e-${idx}`} className="px-2 text-xs text-slate-400">
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => goto(p)}
              className={`h-7 min-w-[28px] px-2 text-xs rounded border ${
                p === safePage
                  ? 'bg-indigo-600 border-indigo-600 text-white font-semibold'
                  : 'border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
              data-testid={`pagination-page-${p}`}
            >
              {p}
            </button>
          )
        )}

        <button
          onClick={() => goto(safePage + 1)}
          disabled={safePage === totalPages}
          className="h-7 w-7 flex items-center justify-center rounded border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pagination-next"
          title="Selanjutnya"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => goto(totalPages)}
          disabled={safePage === totalPages}
          className="h-7 w-7 flex items-center justify-center rounded border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pagination-last"
          title="Halaman terakhir"
        >
          <ChevronsRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
